import { memo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { reviewsAPI } from '../../services/api/reviews'
import { getTextareaClasses } from '../../styles/inputStyles'
import StarRating from '../reviews/StarRating'
import { toast } from '../../services/toastService'

/**
 * BookingReviewPrompt - inline prompt shown after a booking is completed.
 * Links the submitted review to the booking via booking_id.
 * Follows ReviewForm styling conventions.
 * @param {object} booking - booking detail group (needs booking.id)
 * @param {string} entityId
 * @idempotent @atomic
 */
const BookingReviewPrompt = memo(function BookingReviewPrompt({ booking, entityId }) {
  const { t } = useTranslation()
  const { execute, loading } = useAsyncOperation()
  const [done, setDone] = useState(false)
  const [open, setOpen] = useState(false)
  const [rating, setRating] = useState(0)
  const [content, setContent] = useState('')

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault?.()
    if (!rating) return
    await execute(
      () => reviewsAPI.createReview(entityId, { rating, content: content.trim() || undefined, booking_id: booking.id }),
      {
        onSuccess: () => {
          toast.success(t('bookings.reviewSent', 'Review submitted!'))
          setDone(true)
          setOpen(false)
        },
        onError: () => toast.error(t('bookings.reviewFailed', 'Failed to submit review')),
      }
    )
  }, [rating, content, booking.id, entityId, execute, t])

  const handleCancel = useCallback((e) => {
    e?.preventDefault?.()
    setOpen(false)
  }, [])

  if (done) return null

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        onTouchEnd={(e) => { e.preventDefault(); setOpen(true) }}
        className="flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary-700 transition-colors py-1"
      >
        <StarRating rating={0} size="sm" readonly />
        {t('bookings.reviewPrompt', 'Leave a review for this service')}
      </button>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="pt-2 border-t border-gray-100 space-y-2">
      <p className="text-xs font-medium text-gray-700">{t('bookings.rateService', 'Rate your experience')}</p>
      <StarRating rating={rating} onChange={setRating} size="md" />
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder={t('bookings.reviewPlaceholder', 'What did you think?')}
        rows={2}
        className={getTextareaClasses(false)}
        maxLength={1000}
      />
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!rating || loading}
          className="flex-1 py-1.5 px-3 bg-primary text-white text-xs font-semibold rounded-lg disabled:opacity-50 transition-opacity"
        >
          {loading ? t('common.loading', 'Loading...') : t('common.submit', 'Submit')}
        </button>
        <button
          type="button"
          onClick={handleCancel}
          onTouchEnd={handleCancel}
          className="px-3 py-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 rounded-lg border border-gray-200 transition-colors"
        >
          {t('common.cancel', 'Cancel')}
        </button>
      </div>
    </form>
  )
})

export default BookingReviewPrompt

